import { useExchangeStore } from "./exchangeStore";
import BigNumber from "bignumber.js";
import { useIndexPrice } from "./indexprice";
import useTradeStore from "./tradeStore";
import { useTradePosition } from "./tradePosition";
import { useBalance } from "../user/mybalance";
import  useGetCoinTier  from "./coinTier";

export const useGetMarginRatio = (item) => {
    const isCross = item?.cross_iso === 'C' // 교차마진이면 true

    const { getCoinTier } = useGetCoinTier()
    const { data: tradeStore } = useTradeStore(); 
    const { EPs } = useExchangeStore();
    const { getPrice } = useIndexPrice(tradeStore?.ep_id || 0);
    const { data : pos } = useTradePosition()
    const currency = tradeStore?.pairs?.split('/')[1]
    const { data: balance } = useBalance(currency, 1);

    return isCross ? crossMarginRatio() : isolatedMarginRatio()

    // 교차마진 증거금비율 ( 전체 유지증거금 / 선물 지갑 전체 자산 * 100 )
    function crossMarginRatio() {
        // 전체 유지증거금
        const sumMm = pos?.reduce((acc, curr) => {
            const EP = EPs?.find(
                (item2: any) => +item2.id == +curr?.exchange_pairs_id
            );
            const currentPrice = getPrice(curr?.exchange_pairs_id);
            const [coin, _] = EP?.pairs?.split('/')
            const amount = BigNumber(curr?.amount).multipliedBy(curr?.leverage).multipliedBy(currentPrice).toNumber()

            // 유지 증거금 계산
            const { mmr, ma } = getCoinTier(coin, amount)
            const maintenanceMargin = BigNumber(curr?.amount)
                .multipliedBy(curr?.leverage)
                .multipliedBy(curr?.price)
                .multipliedBy(mmr)
                .minus(ma)
                .toNumber()

            return acc + (maintenanceMargin || 0)
        }, 0 ) || 0

        // 선물 지갑 전체 자산
        const totalDeposit = getTotalDeposit(pos, getPrice, balance)

        // 증거금 잔액이 0이거나 음수이면 1로 대체하여 매우 높은 위험도 표시
        // (실제로는 즉시 청산 상태)
        const safeTotalDeposit = totalDeposit > 0 ? totalDeposit : 1

        const ratio = BigNumber(sumMm).dividedBy(safeTotalDeposit).multipliedBy(100).toNumber()

        // NaN 체크 및 음수 방지
        if (isNaN(ratio)) {
            return 0
        }

        return Math.max(0, ratio)
    }

    // 격리마진 증거금비율 ( 유지증거금 / 증거금 잔액 * 100 )
    function isolatedMarginRatio() {
        const EP = EPs?.find(
            (item2: any) => +item2.id == +item?.exchange_pairs_id
        );

        if (!EP || !item) {
            return 0
        }

        const currentPrice = getPrice(item?.exchange_pairs_id);
        const [coin, _] = EP?.pairs?.split('/')

        if (!coin || !currentPrice) {
            return 0
        }

        const amount = BigNumber(item?.amount).multipliedBy(item?.leverage).multipliedBy(currentPrice).toNumber();

        // 유지증거금 계산
        const { mmr, ma } = getCoinTier(coin, amount)
        const maintenanceMargin = BigNumber(item?.amount)
            .multipliedBy(item?.leverage)
            .multipliedBy(item?.price)
            .multipliedBy(mmr)
            .minus(ma)
            .toNumber()

        // 증거금 잔액 ( 증거금 + 미실현 손익 )
        const margin = BigNumber(item?.margin || 0).toNumber()
        const unrealizedplpercent = getUnrealizedplpercent(item, currentPrice, BigNumber(item?.amount).multipliedBy(item?.leverage).toNumber())
        const marginBalance = BigNumber(margin).plus(unrealizedplpercent).toNumber()

        // NaN 체크
        if (isNaN(maintenanceMargin) || isNaN(marginBalance)) {
            return 0
        }

        // mmr이 0이면 티어 정보를 찾지 못한 것
        if (Number(mmr) === 0) {
            return 0
        }

        // 증거금 잔액이 0이거나 음수이면 1로 대체
        // 이는 포지션이 청산 직전 또는 이미 청산 상태임을 의미
        const safeMarginBalance = marginBalance > 0 ? marginBalance : 1

        const ratio = BigNumber(maintenanceMargin).dividedBy(safeMarginBalance).multipliedBy(100).toNumber()

        // NaN 체크 및 음수 방지
        if (isNaN(ratio)) {
            return 0
        }

        return Math.max(0, ratio)
    }
}


// 미실현 손익 계산
function getUnrealizedplpercent(item, currentPrice, amount) {
    let pnl = 0;

    if (item?.long_short === 'L') {
        pnl = BigNumber(currentPrice)
        .minus(item?.price) // 진입가
        .multipliedBy(amount)
        .toNumber();
    } else {
        pnl = BigNumber(item?.price)
        .minus(currentPrice)
        .multipliedBy(amount)
        .toNumber();
    }

    return pnl
}

// 선물 지갑 전체 자산
function getTotalDeposit(openPositions, getPrice, balance) {
    let pnl = BigNumber(0);

    for (let i = 0; i < openPositions?.length; i++) {
        if (openPositions[i].cross_iso == "I") continue;

        const currentPrice = getPrice(openPositions?.[i]?.exchange_pairs_id);
        const openPrice = BigNumber(openPositions?.[i]?.open_price);
        const amount = BigNumber(openPositions[i]?.amount);
        const leverage = BigNumber(openPositions[i]?.leverage);

        if (openPositions[i].long_short === "L") {
            // 롱: (현재가 - 진입가) × 수량 × 레버리지
            pnl = pnl.plus(
                BigNumber(currentPrice).minus(openPrice).multipliedBy(amount).multipliedBy(leverage)
            );
        } else {
            // 숏: (진입가 - 현재가) × 수량 × 레버리지
            pnl = pnl.plus(
                openPrice.minus(currentPrice).multipliedBy(amount).multipliedBy(leverage)
            );
        }
    }

    const total = BigNumber(balance?.balance || 0).plus(pnl).toNumber();
    return isNaN(total) ? 0 : total;
}